// React
import { useEffect, useState } from "react";

// React Query
import { useQuery, useQueryClient } from "@tanstack/react-query";

// MUI
import PaginationItem from "@mui/material/PaginationItem";
import clsx from "clsx";
import { TransitionGroup } from "react-transition-group";
import { Button, Collapse, Grid, Pagination, Skeleton } from "@mui/material";

// API
import { getOEMData } from "@DataPages/api/oem";

// Helpers
import { and, or } from "@Common/helpers/sonar";
import { updateProgramsValueOemHelper } from "@DataPages/helpers/oem/updateProgramsValueOemHelper";

// Components
import OemDataPage from "@DataPages/components/OEM/Tables/OemDataTable/OemDataPage";
import TableLoader from "@Layouts/components/Skeletons/TableLoader";
import SectionContainer from "@Layouts/components/SectionContainer";
import EmptyBody from "@Layouts/components/Basics/EmptyBody";
import { FilterRankingDataPageProps, RowArray } from "@DataPages/components/OEM/OEMTabBody";

// Styles
import "@DataPages/components/OEM/Tables/OemDataTable/OemDataTable.css";

// Assets
import empty from "@/assets/images/commons/empty.png";
import arrowIcon from "@/assets/icons/dataPages/oem/right-arrow-icon.svg";

// Types
import { IOEM } from "@Common/interfaces/location";
import { ICommissionCycle } from "@Common/api/types/CommissionCycle";
import { IOEMPageContent, OEMDocument } from "@DataPages/types/oem/model";

type OemDataPagesProps = Readonly<{
	locationId: string;
	selected: ICommissionCycle;
	oemDataTab: string;
	tableProps?: object;
	isPaginationPage?: boolean;
	oem: string;
	dealerCodeFS: string;
	defaultPage: {
		pageNumber: number | string;
		pageName: string;
		content: string;
	};
	currentPageNumbers?: string[];
	isButtonsPaginationPage?: boolean;
	isFranchisePage?: boolean;
	isRankingPage?: boolean;
	filterRankingDataPage?: ({ content, tableInformation }: Readonly<FilterRankingDataPageProps>) => RowArray;
	locationOEMs?: IOEM[];
	franchiseNames?: string[];
	franchiseName?: string;
	parentsProps?: {
		commissionCycleId: string;
		dealerCodeFS: string;
		locationId: string;
		locationOEMs: IOEM[];
		oemReference: string;
	};
}>;

export default function OemDataPages(props: OemDataPagesProps) {
	const { currentPageNumbers = [], franchiseNames: currentFranchiseNames = [] } = props;

	// Local State
	const [oem, setOem] = useState("");
	const [dealerCodeFS, setDealerCodeFS] = useState("");
	const [currentPage, setCurrentPage] = useState(1);
	const [currentPageNumber, setCurrentPageNumber] = useState("");
	const [currentFranchiseName, setCurrentFranchiseName] = useState("");
	const [dataContentPage, setDataContentPage] = useState<IOEMPageContent | null>(null);

	const queryClient = useQueryClient();

	const parentsProps = { ...props.parentsProps, franchise: currentFranchiseName };

	const { data, isLoading } = useQuery({
		queryKey: [
			"oemData",
			props.locationId,
			props.selected?._id,
			props.oemDataTab,
			oem,
			dealerCodeFS,
			currentFranchiseName,
			currentPageNumber,
		],
		queryFn: async () =>
			getOEMData({
				dealerCodeFS,
				programName: props.oemDataTab,
				locationId: props.locationId,
				commissionCycleId: props.selected?._id,
				oemReference: oem,
				franchiseName: currentFranchiseName,
				page: currentPageNumber,
			}),
		enabled: and(
			!!props.selected?._id,
			!!props.locationId,
			!!props.oemDataTab,
			!!oem,
			!!dealerCodeFS,
			or(currentPageNumber !== dataContentPage?.pageNumber, currentFranchiseName !== props.franchiseName)
		),
		staleTime: Infinity,
	});

	const onUpdateCellTableValue = async (dataToUpdate: unknown, successUpdateCallback: Function, errorUpdateCallback: Function) => {
		const response = await updateProgramsValueOemHelper(props.oem, props.oemDataTab, dataToUpdate);
		if (response?.content) {
			queryClient.setQueryData(["oemData", props.locationId, props.selected?._id, props.oemDataTab], (oldData: OEMDocument) => {
				return {
					...oldData,
					pages: oldData.pages.map((oemPage) => {
						if (oemPage.oemReference === props.oem) {
							return {
								...oemPage,
								page: {
									...oemPage.page,
									content: response.content,
								},
							};
						}
						return oemPage;
					}),
				};
			});
			successUpdateCallback();
		} else {
			errorUpdateCallback();
		}
	};

	const onPageChanged = (_event: unknown, page: number) => {
		setCurrentPage(page);
		setCurrentPageNumber(currentPageNumbers[page - 1]);
	};

	const paginationSection = (
		<Grid
			container
			direction={"row"}
			rowSpacing={2}
			sx={{
				marginY: "35px",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
			}}
		>
			{currentPageNumbers?.length > 1 && (
				<Pagination
					count={currentPageNumbers?.length}
					page={currentPage}
					showFirstButton
					showLastButton
					onChange={onPageChanged}
					classes={{ text: "pagination-theme" }}
					renderItem={({ page, ...item }) => <PaginationItem {...item} page={currentPageNumbers[(page ?? 1) - 1]} />}
				/>
			)}
		</Grid>
	);

	const onButtonPageChanged = (page: string) => {
		setCurrentFranchiseName(page);
		setCurrentPageNumber("");
	};

	const marginY = () => {
		if (currentFranchiseNames?.length > 1) {
			return "16px";
		}
		else if (props.oemDataTab === "CSI") {
			return "0px";
		}
		return "10px";
	}
		
	const buttonsSection = (
		<Grid
			container
			direction={"row"}
			rowSpacing={2}
			sx={{
				marginY: marginY(),
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
			}}
		>
			{currentFranchiseNames?.length > 1 &&
				currentFranchiseNames.map((franchise) => {
					const className = `pagination-data-button${currentFranchiseName === franchise ? " selected" : ""}`;
					return (
						<Button key={franchise} className={className} onClick={() => onButtonPageChanged(franchise)}>
							{franchise}
						</Button>
					);
				})}
		</Grid>
	);

	const navigateButton = (direction: "left" | "right") => {
		if (!props?.isPaginationPage) {
			return null;
		}

		const classNames: { [key: string]: boolean } = { "floating-pagination-btn": true };
		switch (direction) {
			case "left":
				classNames["left-floating-pagination-btn"] = true;
				break;
			case "right":
				classNames["right-floating-pagination-btn"] = true;
				break;
		}

		const nextPage = currentPage + (direction === "right" ? 1 : -1);
		const isDisabled = direction === "right" ? currentPage === currentPageNumbers?.length : currentPage === 1;

		return (
			!isDisabled && (
				<Button className={clsx(classNames)} onClick={() => onPageChanged(null, nextPage)}>
					<img src={arrowIcon} alt="icon" />
				</Button>
			)
		);
	};
	useEffect(() => {
		const dataPage = data?.pages?.find((item) => item.oemReference === oem);
		if (dataPage?.page) {
			setDataContentPage(dataPage.page || []);
		}
	}, [data, oem, dataContentPage]);

	useEffect(() => {
		setOem(props?.oem);
		setDealerCodeFS(props?.dealerCodeFS);
		if (props?.defaultPage) {
			setCurrentPage(1);
			setCurrentPageNumber(props.defaultPage.pageNumber as string);
			setDataContentPage(props.defaultPage as unknown as IOEMPageContent);
		}

		if (props?.franchiseName) {
			setCurrentFranchiseName(props.franchiseName);
		}

		return () => {
			setOem("");
			setDealerCodeFS("");
			setCurrentPage(1);
			setCurrentPageNumber("");
			setCurrentFranchiseName("");
			setDataContentPage(null);
		};
	}, [props]);

	if (isLoading) {
		return (
			<TransitionGroup key="transitionKey" className="px-[5px] min-h-[450px]">
				<Collapse key={1}>
					<SectionContainer title={"Loading"} collapsible loading={true} sx={{ boxShadow: "none !important" }}>
						<Grid item mt={0} xs={12} mb={2} sx={{ padding: "0px 30px" }}>
							<Skeleton variant="rectangular" width={200} height={20} sx={{ borderRadius: "6px", marginTop: "20px" }} />
							<TableLoader content rows={7} />
						</Grid>
					</SectionContainer>
				</Collapse>
			</TransitionGroup>
		);
	}

	if (!dataContentPage) {
		return (
			<EmptyBody
				image={empty}
				title={"No data found"}
				imageStyles={{ width: "330px", marginTop: "65px" }}
				bodyHeight={"320px"}
				sx={{ height: "450px !important" }}
			/>
		);
	}

	return (
		<Grid container>
			{props?.isPaginationPage && paginationSection}
			{props?.isButtonsPaginationPage && buttonsSection}
			<OemDataPage
				data={dataContentPage}
				isPaginationPage={props?.isPaginationPage}
				navigateButton={navigateButton}
				onUpdateCellTableValue={onUpdateCellTableValue}
				parentsProps={parentsProps}
				filterRankingDataPage={props.filterRankingDataPage}
				tableProps={props?.tableProps}
				franchiseName={parentsProps.franchise ?? ""}
				oemDataTab={props.oemDataTab}
			/>
			{props?.isPaginationPage && paginationSection}
		</Grid>
	);
}
